import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDownIcon } from 'lucide-react';
import React from 'react';

import {
  TYPE_SCALE,
  GAP_SCALE,
  PADDING_Y,
  SIZE_SCALE,
  PADDING_BOTTOM,
  COLOR_SCALE,
  FLEX,
  FLEX_ALIGN as FLEX_ALIGN,
  FLEX_JUSTIFY as FLEX_JUSTIFY,
} from '@/components/design-system/tokens';
import { cn } from '@/lib/utils';

type AccordionProps = React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>;

function Accordion({ ...props }: AccordionProps) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

interface AccordionItemProps extends React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Item
> {
  className?: string;
  children?: React.ReactNode;
}

function AccordionItem({ className, ...props }: AccordionItemProps) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn(`border-b ${COLOR_SCALE['accordion-border']} last:border-b-0`, className)}
      {...props}
    />
  );
}

interface AccordionTriggerProps extends React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Trigger
> {
  className?: string;
  children: React.ReactNode;
}

function AccordionTrigger({ className, children, ...props }: AccordionTriggerProps) {
  return (
    <AccordionPrimitive.Header className={FLEX.flex}>
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          `${FLEX.flex} ${FLEX.flex1} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.between} ${GAP_SCALE['4']} ${PADDING_Y['4']} text-left ${TYPE_SCALE.sm} font-semibold transition-all outline-none disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180`,
          className
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon
          className={`${COLOR_SCALE['accordion-icon']} pointer-events-none ${SIZE_SCALE['4']} shrink-0 transition-transform duration-200`}
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

interface AccordionContentProps extends React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Content
> {
  className?: string;
  children: React.ReactNode;
}

function AccordionContent({ className, children, ...props }: AccordionContentProps) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className={`data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden ${TYPE_SCALE.sm} ${COLOR_SCALE['accordion-content']}`}
      {...props}
    >
      <div className={cn(`${PADDING_BOTTOM['4']}`, className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
